import { useState } from 'react';
import { Icon } from '../chrome/Icon';
import { MediaTile } from '../media/MediaTile';
import { MediaLightbox } from '../media/MediaLightbox';
import { useEncryption } from '../../crypto/EncryptionProvider';
import type { QuestionnaireItem, QuestionnaireQuestionDef } from '../../data/dto';
import { useT } from '../../i18n';

interface Props {
  item: QuestionnaireItem;
  questions: QuestionnaireQuestionDef[];
  babyId: string;
  onOpen: () => void;
}

/**
 * Journey-timeline card for a questionnaire. Completed → a compact list of the
 * answers plus any uploaded photos; available → a "fill out" prompt. The text
 * opens the form sheet; photo thumbnails open a lightbox (comments/reactions).
 */
export function QuestionnaireCard({ item, questions, babyId, onOpen }: Props) {
  const t = useT();
  const enc = useEncryption();
  const completed = item.availability === 'completed';
  const media = item.media ?? [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Answered questions, in catalog order, with their labels.
  const answered = completed
    ? questions
        .filter((q) => (item.answers[q.code] ?? '').trim() !== '')
        .map((q) => ({ label: q.label, value: item.answers[q.code] }))
    : [];

  return (
    <div className="w-full max-w-[200px] rounded-2xl border border-line bg-surface px-3.5 py-3 shadow-[0_8px_22px_-14px_rgba(33,28,21,0.22)]">
      <button onClick={onOpen} className="w-full text-left">
        <div className="flex items-center gap-2">
          <div
            className="flex size-7 flex-shrink-0 items-center justify-center rounded-full"
            style={{ background: 'color-mix(in oklch, var(--color-accent) 16%, transparent)' }}
          >
            <Icon name="edit" size={14} color="var(--color-accent)" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-accent-ink">
            {item.title}
          </span>
        </div>

        {completed ? (
          <div className="mt-2 flex flex-col gap-1">
            {answered.slice(0, 4).map((a) => (
              <div key={a.label} className="leading-snug">
                <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-3">
                  {a.label}
                </span>
                <p className="text-[13px] text-ink line-clamp-2">{a.value}</p>
              </div>
            ))}
            {answered.length === 0 && media.length === 0 && (
              <p className="text-[13px] text-ink-2">{t('questionnaire.completed', 'Saved')}</p>
            )}
          </div>
        ) : (
          <div className="mt-1.5 flex items-center gap-1">
            <p className="text-sm font-medium leading-snug text-ink">
              {item.subtitle ?? t('questionnaire.fillOut', 'Fill out')}
            </p>
          </div>
        )}
      </button>

      {media.length > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {media.slice(0, 6).map((m, i) => (
            <MediaTile
              key={m.id}
              media={m}
              canDecrypt={enc.status === 'ready'}
              decrypt={enc.decrypt}
              onOpen={() => setLightboxIndex(i)}
              onUnlock={() => void enc.ensureReady()}
            />
          ))}
        </div>
      )}

      <button onClick={onOpen} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-accent-ink">
        {completed ? t('questionnaire.edit', 'Edit') : t('questionnaire.fillOut', 'Fill out')}
        <Icon name="arrow" size={11} color="var(--color-accent)" />
      </button>

      {lightboxIndex !== null && media[lightboxIndex] && (
        <MediaLightbox
          items={media}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          canDecrypt={enc.status === 'ready'}
          decrypt={enc.decrypt}
          babyId={babyId}
        />
      )}
    </div>
  );
}
